/*
 * Trust Strip v6.0
 *
 * Three small client-side jobs:
 *
 *   1. Rating click → open Loox' all-reviews modal (via the hidden
 *      .loox-trust-badge-container kept inside the section). Silent no-op if
 *      Loox' script didn't load.
 *
 *   2. Ship-day copy, holiday-aware and SYNCED with the In-Stock Dot. Both read
 *      the same global holiday list and the same business-day/cutoff logic, so
 *      the strip can never promise "heute raus" on a weekend, public holiday, or
 *      company closure. The date functions below are ported verbatim from
 *      custom-stock-block.js so the two can never drift. SSR renders a safe,
 *      always-true fallback; JS upgrades it to today / tomorrow / {weekday}.
 *
 *   3. Subtle one-time reveal when the strip scrolls into view (guarded so it
 *      never hides the row without JS; disabled under reduced-motion).
 *
 * Design Mode safety: re-runs on shopify:section:load.
 */
(function () {
  'use strict';

  // ── Rating click → open Loox modal ────────────────────────────────────

  var LOOX_BADGE_SELECTOR = '.loox-trust-badge-container[data-loox-open-reviews="true"]';

  function bindRatingTrigger(strip) {
    var trigger = strip.querySelector('[data-trust-strip-rating-trigger]');
    if (!trigger) return;

    trigger.addEventListener('click', function (event) {
      event.preventDefault();
      var badge = strip.querySelector(LOOX_BADGE_SELECTOR) || document.querySelector(LOOX_BADGE_SELECTOR);
      if (!badge) return;
      findLooxClickTarget(badge).click();
    });
  }

  // Loox renders its trust badge as a web component (<loox-trust-badge-widget>)
  // with an open shadow root since ~September 2026; the clickable element
  // (div[role="button"]) lives inside that shadow root, so a light-DOM
  // querySelector finds nothing and the click went nowhere. Look in both.
  var CLICKABLE = 'a, [role="button"], button';

  function findLooxClickTarget(badge) {
    var inner = badge.querySelector(CLICKABLE);
    if (inner) return inner;
    var hosts = badge.querySelectorAll('*');
    for (var i = 0; i < hosts.length; i++) {
      var root = hosts[i].shadowRoot;
      if (!root) continue;
      inner = root.querySelector('[role="button"]') || root.querySelector(CLICKABLE);
      if (inner) return inner;
    }
    return badge;
  }

  // ── Date helpers (ported from custom-stock-block.js — keep in sync) ────

  function parseCutoffToHours(value) {
    if (value === null || value === undefined || value === '') return 10.5;
    var str = String(value).trim();
    var m = str.match(/^(\d{1,2}):(\d{2})$/);
    if (m) {
      var h = parseInt(m[1], 10);
      var min = parseInt(m[2], 10);
      if (h >= 0 && h <= 23 && min >= 0 && min <= 59) return h + min / 60;
    }
    var dec = parseFloat(str);
    if (!isNaN(dec) && dec >= 0 && dec <= 24) return dec;
    return 10.5;
  }

  /* Reuses the In-Stock Dot's ?stockblock_time= param so the dot and the strip
     simulate the same moment together during testing. */
  function getSimulatedNow() {
    try {
      var p = new URLSearchParams(window.location.search).get('stockblock_time');
      if (p) {
        var sim = new Date(p);
        if (!isNaN(sim.getTime())) return sim;
      }
    } catch (e) {}
    return new Date();
  }

  function parseHolidays(str) {
    if (!str) return [];
    var normalized = String(str)
      .replace(/&amp;#10;/g, ',')
      .replace(/&#10;/g, ',')
      .replace(/&amp;#13;/g, '')
      .replace(/&#13;/g, '')
      .replace(/\r?\n/g, ',')
      .replace(/\s+/g, ',');
    return normalized
      .split(',')
      .map(function (h) { return h.trim(); })
      .filter(function (h) { return /^\d{4}-\d{2}-\d{2}$/.test(h); });
  }

  function isHoliday(date, holidays) {
    return holidays.indexOf(date.toISOString().split('T')[0]) !== -1;
  }

  function isBusinessDay(date, holidays) {
    var day = date.getDay();
    return day !== 0 && day !== 6 && !isHoliday(date, holidays);
  }

  function getNextBusinessDay(fromDate, skipToday, holidays) {
    var date = new Date(fromDate);
    if (skipToday) date.setDate(date.getDate() + 1);
    var safety = 30;
    while (!isBusinessDay(date, holidays) && safety-- > 0) {
      date.setDate(date.getDate() + 1);
    }
    return date;
  }

  // ── Ship-day copy ─────────────────────────────────────────────────────

  function renderShipping(strip, config) {
    /* Two slots may exist (long/short historically); update all in sync. */
    var slots = strip.querySelectorAll('[data-trust-strip-shipping]');
    if (!slots.length) return;

    /* Busy period: leave the safe SSR fallback rather than promise a ship-day. */
    if (config.busy) return;

    var holidays = parseHolidays(config.holidays);
    var cutoff = parseCutoffToHours(config.cutoffTime);
    var rawNow = getSimulatedNow();
    var now = new Date(rawNow.toLocaleString('en-US', { timeZone: config.timezone }));
    var currentHour = now.getHours() + now.getMinutes() / 60;
    var labels = config.labels || {};
    var locale = config.locale || 'de';

    var text;
    if (isBusinessDay(now, holidays) && currentHour < cutoff) {
      text = labels.today;
    } else {
      var ships = getNextBusinessDay(now, true, holidays);
      var tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (ships.toDateString() === tomorrow.toDateString()) {
        text = labels.tomorrow;
      } else {
        var dayName;
        try {
          dayName = ships.toLocaleDateString(locale, { weekday: 'long' });
        } catch (e) {
          dayName = ships.toLocaleDateString('en-US', { weekday: 'long' });
        }
        text = (labels.weekday || '[day]').replace('[day]', dayName);
      }
    }

    if (!text) return;
    for (var i = 0; i < slots.length; i += 1) {
      slots[i].textContent = text;
    }
  }

  // ── Reveal on scroll-in (once) ────────────────────────────────────────

  function revealOnScroll(strip) {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
      strip.classList.add('is-visible');
      return;
    }

    /* Arm the hidden state only now (CSS hides the row only under [data-reveal]),
       so the row is never hidden when JS is absent. */
    strip.setAttribute('data-reveal', '');

    var revealed = false;
    function reveal() {
      if (revealed) return;
      revealed = true;
      strip.classList.add('is-visible');
    }

    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i += 1) {
        if (entries[i].isIntersecting) {
          reveal();
          io.disconnect();
          break;
        }
      }
    }, { threshold: 0.25 });
    io.observe(strip);

    /* Safety net: never leave the row hidden if the observer never fires. */
    setTimeout(reveal, 4000);
  }

  // ── Init / lifecycle ──────────────────────────────────────────────────

  function init(strip) {
    if (strip.dataset.trustStripBooted === 'true') return;
    strip.dataset.trustStripBooted = 'true';

    bindRatingTrigger(strip);
    revealOnScroll(strip);

    var configEl = document.querySelector('script[data-trust-strip-shipping-config]');
    if (configEl) {
      try {
        var config = JSON.parse(configEl.textContent);
        renderShipping(strip, config);
      } catch (e) {
        /* malformed config — leave SSR fallback in place */
      }
    }

    function onUnload(event) {
      if (event && event.target && event.target.contains(strip)) {
        strip.dataset.trustStripBooted = '';
        document.removeEventListener('shopify:section:unload', onUnload);
      }
    }
    document.addEventListener('shopify:section:unload', onUnload);
  }

  function boot() {
    var strips = document.querySelectorAll('[data-trust-strip]');
    for (var i = 0; i < strips.length; i += 1) {
      init(strips[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var strips = event.target ? event.target.querySelectorAll('[data-trust-strip]') : [];
    for (var i = 0; i < strips.length; i += 1) {
      init(strips[i]);
    }
  });
})();
